import { Callout, DirectionalHint, Icon, IconButton, Layer, Overlay } from "@fluentui/react";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { dayAndTime, isGatewaySentinel, sensorName, sentinelWhere, nameLabel } from "../../Utils/DeviceTexts";
import { getTimeSinceLastUpdate, isSensorOffline } from "../../Utils/SensorStatus";
import { OFFLINE_HINT } from "../DeviceCard/DeviceCard";
import "./DeviceDetails.scss";

export interface IDeviceDetailsProps {
  sentinel: ISentinelRich;
  // elemento toccato: il pannello si apre accanto
  target: HTMLElement;
  // solo questo sensore; senza, tutti i sensori del dispositivo
  sensor?: ISensor;
  // tipi di sensore con un allarme aperto
  alarmTypes: string[];
  // tipi di sensore con solo allarmi di prova
  testTypes?: string[];
  onDismiss: () => void;
}

// Pannello «Dettagli» che si apre al tocco (sul telefono i suggerimenti al passaggio del mouse non esistono)
const DeviceDetails = (props: IDeviceDetailsProps) => {
  const sensors = props.sensor
    ? [props.sensor]
    : props.sentinel.sensors.filter((x: ISensor) => x.type !== "pesogr2");
  const gateway = isGatewaySentinel(props.sentinel);
  const silent = props.sentinel.enabled && sensors.some((x: ISensor) => isSensorOffline(x));

  const state = (x: ISensor) => {
    if (!props.sentinel.enabled || !x.enabled) return <span className="device-details-pill grey">Spento</span>;
    if (props.alarmTypes.includes(x.type)) return <span className="device-details-pill alarm">Allarme</span>;
    if (props.testTypes?.includes(x.type)) return <span className="device-details-pill test">Prova</span>;
    if (isSensorOffline(x)) return <span className="device-details-pill warn">Non risponde</span>;
    return <span className="device-details-pill ok">Acceso</span>;
  };

  // il pannello si apre SOPRA la sua card, coprendola (stessa posizione e larghezza): non nasconde gli
  // altri dispositivi (scelta del titolare, 01/11/2026); un clic fuori lo chiude
  const card = (props.target?.closest?.(".device-card") as HTMLElement | null) ?? null;
  return (
    <>
    {/* il resto della pagina si scurisce finche' il pannello e' aperto; un clic sul fondo lo chiude */}
    <Layer>
      <Overlay className="device-details-overlay" styles={{ root: { position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.6)" } }} onClick={props.onDismiss} />
    </Layer>
    <Callout
      target={card ?? props.target}
      coverTarget={!!card}
      calloutWidth={card ? card.offsetWidth : undefined}
      isBeakVisible={!card}
      gapSpace={0}
      onDismiss={props.onDismiss}
      directionalHint={card ? DirectionalHint.topLeftEdge : DirectionalHint.bottomCenter}
      directionalHintFixed={!!card}
      setInitialFocus
      role="dialog"
      ariaLabel={"Dettagli di " + nameLabel(props.sentinel.patient.identifier)}
      className="device-details"
    >
      <div className="device-details-inner">
        <div className="device-details-head">
          <div className="device-details-title">
            <div className="device-details-name">{nameLabel(props.sentinel.patient.identifier)}</div>
            <div className="device-details-where">{sentinelWhere(props.sentinel)}</div>
          </div>
          <IconButton iconProps={{ iconName: "Cancel" }} title="Chiudi" ariaLabel="Chiudi" onClick={props.onDismiss} />
        </div>
        {silent && (
          <div className="device-details-note">
            <Icon iconName="Warning" />
            Non risponde. {OFFLINE_HINT}
          </div>
        )}
        <dl className="device-details-list">
          {sensors.map((x: ISensor) => (
            <div key={x.id} className="device-details-row">
              <dt>{sensorName(x.type)}</dt>
              <dd>
                {state(x)}
                <span className="device-details-dim">
                  ultimo dato {dayAndTime(x.updatedAt)} ({getTimeSinceLastUpdate(x.updatedAt)})
                </span>
              </dd>
            </div>
          ))}
          {!props.sensor && (
            <div className="device-details-facts">
              <div className="device-details-row">
                <dt>Telecamera</dt>
                <dd>{props.sentinel.cameraUrl && props.sentinel.cameraUrl.trim() !== "" ? "Sì" : "No"}</dd>
              </div>
              <div className="device-details-row">
                <dt>Audio</dt>
                <dd>{props.sentinel.audioEnabled ? "Sì" : "No"}</dd>
              </div>
              <div className="device-details-row">
                <dt>{gateway ? "ID Varco" : "ID Letto"}</dt>
                <dd>{props.sentinel.id}</dd>
              </div>
            </div>
          )}
        </dl>
      </div>
    </Callout>
    </>
  );
};

export default DeviceDetails;
